<template>
  <div
    :class="[$style.resource_item_container, focused ? $style.focused : '']"
    @mousedown.stop="onClick"
  >
    <div :class="$style.select_box">
      <vgp-checkbox
        :checked="selected"
        :size="18"
      />
    </div>

    <div :class="$style.info_box">
      <vgp-avatar
        :class="$style.resource_item_icon"
        :src="picture"
        :custom-size="22"
      />

      <div :class="$style.resource_item_info">
        {{ formatName(name) }}
      </div>
    </div>

    <div
      v-if="!isHideValuesInTemplates && isTimeResource"
      :class="[$style.resource_input_container, hasPricingAccessToEstimation ? '' : 'pricing-tooltip']"
      :data-position="hasPricingAccessToEstimation ? null : 'top'"
      :data-additional-dif="hasPricingAccessToEstimation ? null : resourceId"
      :data-feature="hasPricingAccessToEstimation ? null : 'estimation_assign'"
    >
      <vgp-input
        :model-value="valuePerDay"
        :class="$style.input_box"
        :is-disabled="blockPerDayInput || !selected || !hasPricingAccessToEstimation"
        @onChange="onChangeValuePerDay"
      />

      <span :class="$style.label_box">{{ locales(labelPerDay) }}</span>

      <vgp-input
        :model-value="value"
        :class="$style.input_box"
        :is-disabled="!selected || !hasPricingAccessToEstimation"
        @onChange="onChangeValue"
      />

      <span :class="$style.label_box">{{ locales(getLabelValueByResourceType(resourceType)) }}</span>

      <div :class="$style.resource_days">
        <span :class="[$style.label_box, $style.all_days]">
          {{ `${workDaysCount} ${locales('resource_all_days')}` }}
        </span>

        <span
          v-if="hasTooltip"
          :class="[$style.tooltip_box, 'tooltip-gantt']"
          data-key="resource_has_customs"
        />

        <span
          v-if="isOverloaded"
          :class="[$style.overload_box, 'tooltip-gantt']"
          data-key="overload_tooltip_assign"
        >
          <span v-html="overloadIcon" />
        </span>
      </div>
    </div>

    <div
      v-else-if="!isHideValuesInTemplates"
      :class="$style.resource_input_container"
    >
      <vgp-input
        :model-value="value"
        :class="$style.input_box"
        :is-disabled="!selected"
        @onChange="onChangeValue"
      />

      <span :class="$style.label_box">{{ locales(getLabelValueByResourceType(resourceType)) }}</span>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import vgpCheckbox from '$$vueCmp/checkbox/vgpCheckbox.vue';
import vgpAvatar from '$$vueCmp/avatar/vgpAvatar.vue';
import vgpInput from '$$vueCmp/vInput/vgpInput.vue';
import { checkPricingAccess } from '../../helpers/pricingHelper';
import projectsModel from '$$projectModel';
import routerHelper from '$$routerHelper';
import globalStore from '$$globalStore';
import customHelper from '../../helpers/custom';
import rights from '../../components/rights';
import constants from '$$helpers/constants';
import resourcesOverload from '../../components/resourcesOverload';
import app from '$$app';

const overloadIcon = `
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <use xmlns:xlink="http://www.w3.org/2000/svg"
    xlink:href="#icons/regular/24/resource"
    href="#icons/regular/24/resource"
  ></use>
</svg>
`;

export default {
  name: 'TaskResourceItem',
  components: {
    vgpCheckbox,
    vgpAvatar,
    vgpInput,
  },
  props: {
    taskId: { type: Number, required: true },
    resourceId: { type: Number, required: true },
    ganttId: { type: Number, required: true },
    resourceType: { type: Number, required: true },
    taskType: { type: String, required: true },
    selected: { type: Boolean, required: true },
    picture: { type: String, required: true },
    name: { type: String, required: true },
    valuePerDay: { type: Number, required: false, default: 0 },
    value: { type: [String, Number], required: true },
    workDaysCount: { type: Number, required: true },
    customDaysCount: { type: Number, required: true },
    focused: { type: Boolean, default: false },
  },
  emits: [
    'onChange',
    'onClickResource',
  ],
  data() {
    return {
      locales: __,
      eventsListeners: [],
      hasPricingAccessToEstimation: false,
      isTimeResource: false,
      labelPerDay: '',
      hasTooltip: false,
      isHideValuesInTemplates: true,
      blockPerDayInput: true,
      overloadIcon,
    };
  },
  created() {
    this.addEventListeners();
    let workloadActivity = globalStore.getters['user/getActivitySettings'](constants.USER_ACTIVITIES.WORKLOAD);
    const contextId = +routerHelper.getCurrentRoute().params.projectId || +routerHelper.getCurrentRoute().params.multiviewID || 'global';
    let workloadViewMode = constants.WORKLOAD_VIEW_MODES.hours

    if (workloadActivity && workloadActivity.viewMode) {
      workloadViewMode = workloadActivity.viewMode[contextId];
    }

    const projectConfig = projectsModel.getProjectConfig(this.ganttId);
    const isHideCostValues = this.resourceType === constants.RESOURCE_COST_TYPE && (!rights.project.hasCostRight(this.ganttId) || !projectConfig.auto_budget);

    this.hasPricingAccessToEstimation = checkPricingAccess('estimation');
    this.isOverloaded = resourcesOverload.getOverloadedResources({ taskId: this.taskId, resourceId: this.resourceId });
    this.isTimeResource = this.resourceType === constants.RESOURCE_TIME_TYPE;
    this.hasTooltip = this.resourceType === constants.RESOURCE_TIME_TYPE && this.customDaysCount;

    this.labelPerDay = this.getLabelPerDayByLoadingType(workloadViewMode);
    this.blockPerDayInput = +projectConfig.estimation_mode === constants.WORKLOAD_CALCULATION_MODES.fixEstimation;
    this.isHideValuesInTemplates = isHideCostValues || (this.resourceType === constants.RESOURCE_TIME_TYPE && this.taskType === constants.TASK_TYPES.milestone);
  },
  beforeUnmount() {
    this.removeEventsListeners();
  },
  methods: {
    formatName(str) {
      return str.replace(/>/g, '&gt;').replace(/</g, '&lt;');
    },

    onChangeValuePerDay(event) {
      if (customHelper.testNumber(event.target.value)) {
        this.$emit('onChange', this.resourceId, null, +event.target.value);
      } else if (event.target.value && event.target.value.length) {
        _.set(event, 'target.value', this.valuePerDay);
      }
    },

    onChangeValue(event) {
      if (customHelper.testNumber(event.target.value)) {
        this.$emit('onChange', this.resourceId, +event.target.value, null);
      } else if (event.target.value && event.target.value.length) {
        _.set(event, 'target.value', this.value);
      }
    },

    onClick(event) {
      if (event.target?.nodeName !== 'INPUT') {
        this.$emit('onClickResource', this.resourceId);
      }
    },

    getLabelValueByResourceType(type) {
      switch (type) {
      case constants.RESOURCE_TIME_TYPE:
        return 'resource_time_type';
      case constants.RESOURCE_ITEM_TYPE:
        return 'resource_item_type';
      case constants.RESOURCE_COST_TYPE:
        return 'resource_cost_type';
      }
    },

    getLabelPerDayByLoadingType(type) {
      switch (type) {
      case constants.WORKLOAD_VIEW_MODES.percents:
        return 'resource_percents_per_day';
      default:
        return 'resource_per_day';
      }
    },

    addEventListeners() {
      this.removeEventsListeners();
      this.eventsListeners.push(app.on('overload-cache:updated', () => {
        this.isOverloaded = resourcesOverload.getOverloadedResources({ taskId: this.taskId, resourceId: this.resourceId });
      }));
    },

    removeEventsListeners() {
      this.eventsListeners.forEach(id => app.off(id));
      this.eventsListeners = [];
    },
  },
};
</script>

<style module src="./styles.less" lang="less"></style>
